import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { AccExpense, AccSubmission } from "./accounting";
import type { AccountingSummary, Period } from "./summary";

/** Calls an edge function and turns its error body into a readable message. */
const call = async <T>(name: string, body: unknown, fallback: string) => {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(name, {
    method: "POST",
    body: body as Record<string, unknown>,
  });
  if (error || !data) {
    let message = fallback;
    try {
      const response = await (error as { context?: Response })?.context?.json();
      if (response?.message) message = response.message;
    } catch {
      // keep the generic message
    }
    throw new Error(message);
  }
  return data;
};

export type InvoiceReadResult = {
  expense?: AccExpense;
  /** Already saved: same supplier and invoice number */
  duplicate?: Pick<
    AccExpense,
    "id" | "supplier_name" | "invoice_number" | "invoice_date" | "total"
  >;
  /** Meta receipt added to the Meta Ads expense of its month */
  attached?: AccExpense;
};

/** The AI reads an invoice already uploaded to the bucket and creates the expense. */
export const readInvoice = (path: string, name: string) =>
  call<InvoiceReadResult>(
    "invoice_read",
    { path, name },
    "No se ha podido leer la factura",
  );

export type MetaStatus = {
  connected: boolean;
  accounts: {
    id: string;
    ok: boolean;
    name?: string;
    currency?: string;
    error?: string;
  }[];
};

export const metaStatus = () =>
  call<MetaStatus>(
    "meta_ads_sync",
    { action: "status" },
    "No se ha podido conectar con Meta",
  );

export const metaSync = (since?: string) =>
  call<{ inserted: number; updated: number; skipped: number }>(
    "meta_ads_sync",
    { action: "sync", since },
    "No se ha podido leer el gasto de Meta",
  );

/** Totals sent with the email, from the same summary the page shows. */
export const totalsOf = (summary: AccountingSummary) => ({
  incomeBase: summary.incomeBase,
  incomeVat: summary.incomeVat,
  stripeBase: summary.stripeBase,
  manualBase: summary.manualBase,
  incomeCount: summary.incomeCount,
  expenseBase: summary.expenseBase,
  expenseCount: summary.expenseCount,
  result: summary.result,
  salesVat: summary.vat.salesVat,
  reverseBase: summary.vat.reverseBase,
  reverseVat: summary.vat.reverseVat,
  domesticBase: summary.vat.domesticBase,
  domesticVat: summary.vat.domesticVat,
  deductibleVat: summary.vat.deductibleVat,
  outputVat: summary.vat.outputVat,
  vatBalance: summary.vat.balance,
});

export const sendToGestor = (body: {
  period: Period;
  workbookPath: string;
  zipPath: string;
  summary: AccountingSummary;
  pending: string[];
  message?: string;
}) =>
  call<{
    sent_to: string[];
    attached_zip: boolean;
    submission?: AccSubmission;
  }>(
    "accounting_send",
    {
      year: body.period.year,
      quarter: body.period.quarter,
      workbook_path: body.workbookPath,
      zip_path: body.zipPath,
      totals: totalsOf(body.summary),
      pending: body.pending,
      message: body.message,
      counts: {
        expenses: body.summary.expenseCount,
        incomes: body.summary.incomeCount,
      },
    },
    "No se ha podido enviar al gestor",
  );
